import React, { useState, useEffect } from 'react';
import { 
  Sparkles, Search, Filter, ChevronDown, Clock, CheckCircle, 
  XCircle, Phone, Mail, MessageSquare, RefreshCw, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'Nieuw', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  in_review: { label: 'In Behandeling', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  added: { label: 'Toegevoegd', color: 'text-green-500', bg: 'bg-green-500/10' },
  rejected: { label: 'Afgewezen', color: 'text-red-500', bg: 'bg-red-500/10' }
};

const AdminSuggestions = ({ token, isManager = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const endpoint = isManager ? '/manager/suggestions' : '/admin/suggestions';
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data);
    } catch (error) {
      toast.error('Fout bij laden suggesties');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (suggestionId, newStatus) => {
    setUpdatingId(suggestionId);
    try {
      const endpoint = isManager 
        ? `/manager/suggestions/${suggestionId}/status`
        : `/admin/suggestions/${suggestionId}/status`;
      
      await axios.put(`${API}${endpoint}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestions(prev => prev.map(s => 
        s.id === suggestionId ? { ...s, status: newStatus } : s
      ));
      toast.success('Status bijgewerkt');
    } catch (error) {
      toast.error('Fout bij bijwerken status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredSuggestions = suggestions.filter(s => {
    const matchesSearch = 
      s.perfume_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: suggestions.length,
    new: suggestions.filter(s => s.status === 'new').length,
    in_review: suggestions.filter(s => s.status === 'in_review').length,
    added: suggestions.filter(s => s.status === 'added').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-[#A8A29E] text-sm">Totaal</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-[#A8A29E] text-sm">Nieuw</p>
          <p className="text-2xl font-bold text-blue-500">{stats.new}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-[#A8A29E] text-sm">In Behandeling</p>
          <p className="text-2xl font-bold text-yellow-500">{stats.in_review}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-[#A8A29E] text-sm">Toegevoegd</p>
          <p className="text-2xl font-bold text-green-500">{stats.added}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Zoek op parfum, merk of email..."
            className="input-luxury w-full pl-10"
          />
        </div>
        <div className="relative">
          <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-luxury pl-10 pr-8 appearance-none cursor-pointer"
          >
            <option value="all">Alle Statussen</option>
            <option value="new">Nieuw</option>
            <option value="in_review">In Behandeling</option>
            <option value="added">Toegevoegd</option>
            <option value="rejected">Afgewezen</option>
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#57534E] pointer-events-none" />
        </div>
        <button
          onClick={loadSuggestions}
          className="btn-secondary flex items-center justify-center gap-2"
        >
          <RefreshCw size={18} />
          Vernieuwen
        </button>
      </div>

      {/* Suggestions List */}
      {filteredSuggestions.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Sparkles size={48} className="mx-auto text-[#57534E] mb-4" />
          <h3 className="text-white font-heading text-lg mb-2">Geen suggesties gevonden</h3>
          <p className="text-[#A8A29E] text-sm">
            {searchTerm || statusFilter !== 'all' 
              ? 'Probeer andere filters'
              : 'Klanten hebben nog geen parfum suggesties gedaan'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => (
            <div 
              key={suggestion.id} 
              className="glass rounded-xl p-6"
              data-testid={`suggestion-${suggestion.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                {/* Main Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-heading text-lg">
                          {suggestion.perfume_name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs ${statusConfig[suggestion.status]?.bg} ${statusConfig[suggestion.status]?.color}`}>
                          {statusConfig[suggestion.status]?.label || suggestion.status}
                        </span>
                      </div>
                      {suggestion.brand && (
                        <p className="text-[#D4AF37] text-sm">{suggestion.brand}</p>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-[#A8A29E]">
                      <Mail size={14} />
                      <a href={`mailto:${suggestion.email}`} className="hover:text-[#D4AF37]">
                        {suggestion.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-[#A8A29E]">
                      <Phone size={14} />
                      <a href={`tel:${suggestion.phone}`} className="hover:text-[#D4AF37]">
                        {suggestion.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-[#57534E]">
                      <Clock size={14} />
                      {new Date(suggestion.created_at).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  {/* Notes */}
                  {suggestion.notes && (
                    <div className="flex items-start gap-2 bg-[#1C1917] rounded-lg p-3">
                      <MessageSquare size={14} className="text-[#57534E] mt-0.5 flex-shrink-0" />
                      <p className="text-[#A8A29E] text-sm">{suggestion.notes}</p>
                    </div>
                  )}

                  {/* Language Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-[#57534E] text-xs">Taal:</span>
                    <span className="text-[#A8A29E] text-xs uppercase bg-[#1C1917] px-2 py-0.5 rounded">
                      {suggestion.language || 'nl'}
                    </span>
                  </div>
                </div>

                {/* Status Actions */}
                <div className="flex flex-col gap-2 lg:w-48">
                  <p className="text-[#57534E] text-xs mb-1">Status wijzigen:</p>
                  
                  <button
                    onClick={() => updateStatus(suggestion.id, 'in_review')}
                    disabled={updatingId === suggestion.id || suggestion.status === 'in_review'}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      suggestion.status === 'in_review'
                        ? 'bg-yellow-500/20 text-yellow-500 cursor-default'
                        : 'bg-[#1C1917] text-[#A8A29E] hover:bg-yellow-500/10 hover:text-yellow-500'
                    }`}
                  >
                    {updatingId === suggestion.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Clock size={14} />
                    )}
                    In Behandeling
                  </button>

                  <button
                    onClick={() => updateStatus(suggestion.id, 'added')}
                    disabled={updatingId === suggestion.id || suggestion.status === 'added'}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      suggestion.status === 'added'
                        ? 'bg-green-500/20 text-green-500 cursor-default'
                        : 'bg-[#1C1917] text-[#A8A29E] hover:bg-green-500/10 hover:text-green-500'
                    }`}
                  >
                    {updatingId === suggestion.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle size={14} />
                    )}
                    Toegevoegd
                  </button>

                  <button
                    onClick={() => updateStatus(suggestion.id, 'rejected')}
                    disabled={updatingId === suggestion.id || suggestion.status === 'rejected'}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      suggestion.status === 'rejected'
                        ? 'bg-red-500/20 text-red-500 cursor-default'
                        : 'bg-[#1C1917] text-[#A8A29E] hover:bg-red-500/10 hover:text-red-500'
                    }`}
                  >
                    {updatingId === suggestion.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <XCircle size={14} />
                    )}
                    Afwijzen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSuggestions;
